/**
 * The microphone for a visit, owned by the module rather than by a screen.
 *
 * This used to be a `useRef` inside each visit page, which meant the recorder
 * died on every route change: check in on OC Pending Visits, walk over to any
 * other page, and the audio was gone — check-out still succeeded, silently, with
 * nothing to listen back to. Module scope outlives the components, so one visit
 * keeps recording until it is checked out, wherever the rep navigates.
 *
 * Audio is pushed to the server every few seconds instead of being held in
 * memory until check-out. If the app is reloaded or Android kills the WebView
 * mid-visit, everything up to the last flush is already saved.
 */

import { visitsApi } from '../services/api';

/** How often captured audio is pushed to the server. */
const FLUSH_MS = 10000;
/** MediaRecorder slice size — small enough that a kill loses ~1s, not the visit. */
const SLICE_MS = 1000;
/** Survives a reload so an interrupted visit can pick the mic back up. */
const RESUME_KEY = 'abs.visitRecording';

export interface RecorderState {
    /** The visit currently being recorded, or null when the mic is idle. */
    visitId: number | null;
    /** Seconds elapsed, for the on-screen timer. */
    seconds: number;
    /** Set when a flush failed; cleared on the next success. */
    uploadError: string | null;
}

interface ResumeRecord {
    visitId: number;
    session: string;
    seq: number;
}

let recorder: MediaRecorder | null = null;
let stream: MediaStream | null = null;
let activeVisitId: number | null = null;
let session = '';
let seq = 0;
/** Captured but not yet accepted by the server. Never dropped on a failed flush. */
let pending: Blob[] = [];
let flushTimer: ReturnType<typeof setInterval> | null = null;
let tickTimer: ReturnType<typeof setInterval> | null = null;
let seconds = 0;
let uploadError: string | null = null;
let flushing = false;

const listeners = new Set<(s: RecorderState) => void>();

const snapshot = (): RecorderState => ({ visitId: activeVisitId, seconds, uploadError });
const emit = () => { const s = snapshot(); listeners.forEach(fn => fn(s)); };

/** Subscribe a component to recorder state. Returns an unsubscribe function. */
export function subscribeToRecorder(fn: (s: RecorderState) => void): () => void {
    listeners.add(fn);
    fn(snapshot());
    return () => { listeners.delete(fn); };
}

export const getRecordingVisitId = () => activeVisitId;

const readResume = (): ResumeRecord | null => {
    try {
        const raw = localStorage.getItem(RESUME_KEY);
        return raw ? JSON.parse(raw) as ResumeRecord : null;
    } catch { return null; }
};

const clearResume = () => {
    try { localStorage.removeItem(RESUME_KEY); } catch { /* nothing to clear */ }
};

const writeResume = () => {
    try {
        if (activeVisitId === null) localStorage.removeItem(RESUME_KEY);
        else localStorage.setItem(RESUME_KEY, JSON.stringify({ visitId: activeVisitId, session, seq } as ResumeRecord));
    } catch { /* private mode or storage disabled — recording still works, resume does not */ }
};

/**
 * Open the microphone, or throw with something the rep can act on.
 *
 * Check-in calls this FIRST and does not proceed if it throws: a visit that
 * cannot be recorded must not be checked in.
 */
export async function acquireMic(): Promise<MediaStream> {
    if (!navigator.mediaDevices?.getUserMedia) {
        throw new Error(!window.isSecureContext
            ? 'The microphone needs a secure (https) connection, so this visit cannot be checked in from here.'
            : 'This device blocked microphone access, so this visit cannot be checked in.');
    }
    try {
        return await navigator.mediaDevices.getUserMedia({
            audio: { channelCount: 1, sampleRate: 8000, echoCancellation: true, noiseSuppression: true, autoGainControl: true },
        });
    } catch (e: any) {
        const denied = e?.name === 'NotAllowedError' || e?.name === 'SecurityError';
        throw new Error(denied
            ? 'Microphone permission is required to check in. Allow the microphone in your device settings and try again.'
            : `Could not start the microphone (${e?.name || 'unknown error'}). Check-in needs the microphone, so try again.`);
    }
}

/**
 * Push everything captured so far to the server.
 *
 * On failure the slices stay in `pending` and `seq` does not advance, so the
 * next flush resends them in order and the file on disk stays contiguous.
 */
function uploadBatch(visitId: number, sess: string, at: number, batch: Blob[]): Promise<unknown> {
    const blob = new Blob(batch, { type: batch[0].type || 'audio/webm' });
    return visitsApi.uploadRecordingChunk(visitId, blob, sess, at);
}

async function flush(): Promise<void> {
    if (flushing || pending.length === 0 || activeVisitId === null) return;
    flushing = true;
    const visitId = activeVisitId;
    const batch = pending;
    pending = [];
    try {
        await uploadBatch(visitId, session, seq, batch);
        seq += 1;
        writeResume();
        if (uploadError) { uploadError = null; emit(); }
    } catch (e: any) {
        // Put the bytes back at the front — order matters, the server appends.
        pending = [...batch, ...pending];
        uploadError = e?.message || 'Could not upload audio';
        emit();
    } finally {
        flushing = false;
    }
}

function teardown() {
    if (flushTimer) { clearInterval(flushTimer); flushTimer = null; }
    if (tickTimer) { clearInterval(tickTimer); tickTimer = null; }
    stream?.getTracks().forEach(t => t.stop());
    stream = null;
    recorder = null;
    activeVisitId = null;
    seconds = 0;
    pending = [];
    clearResume();
    emit();
}

/**
 * Begin recording a visit. `mic` is the stream check-in already opened, so the
 * rep is not prompted twice.
 */
/**
 * Close out whichever visit holds the mic, synchronously, so a new one starts
 * clean.
 *
 * This has to finish before the new recorder is built: an async teardown racing
 * a `startVisitRecording` would stop the stream and clear the resume record of
 * the visit that just started. Whatever is already captured ships under the old
 * visit's session — it may lose the last second, but that visit already has a
 * recording on the server and stays checkoutable.
 */
function handOffActiveVisit() {
    const oldId = activeVisitId;
    const oldSession = session;
    const oldSeq = seq;
    const oldBatch = pending;      // requestData() below appends into this array
    const oldStream = stream;
    const rec = recorder;

    if (flushTimer) { clearInterval(flushTimer); flushTimer = null; }
    if (tickTimer) { clearInterval(tickTimer); tickTimer = null; }
    try { if (rec && rec.state !== 'inactive') { rec.requestData(); rec.stop(); } } catch { /* already gone */ }
    oldStream?.getTracks().forEach(t => t.stop());

    recorder = null;
    stream = null;
    activeVisitId = null;
    pending = [];

    if (oldId !== null && oldBatch.length > 0) {
        void uploadBatch(oldId, oldSession, oldSeq, oldBatch).catch(() => { /* surfaced at its own check-out */ });
    }
}

export function startVisitRecording(visitId: number, mic: MediaStream, resumed = false) {
    // Two visits cannot share one mic.
    if (activeVisitId !== null && activeVisitId !== visitId) handOffActiveVisit();

    const prior = resumed ? readResume() : null;
    // A resumed visit keeps its original session id so the new audio appends to
    // the file that already holds the first half. MediaRecorder starts a fresh
    // WebM header on resume, so players may stop at the join — the audio from
    // before the interruption is what matters, and it is already safe on disk.
    const continuing = prior?.visitId === visitId;
    session = continuing ? prior!.session : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
    seq = continuing ? prior!.seq : 0;

    stream = mic;
    activeVisitId = visitId;
    pending = [];
    seconds = 0;
    uploadError = null;

    const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : '';
    // 8 kbps opus — telephone quality, ~60 KB/min, plenty for a conversation.
    recorder = new MediaRecorder(mic, mimeType ? { mimeType, audioBitsPerSecond: 8000 } : { audioBitsPerSecond: 8000 });
    recorder.ondataavailable = (e) => { if (e.data.size > 0) pending.push(e.data); };
    recorder.start(SLICE_MS);

    flushTimer = setInterval(() => { void flush(); }, FLUSH_MS);
    tickTimer = setInterval(() => { seconds += 1; emit(); }, 1000);
    writeResume();
    emit();
}

/**
 * Stop recording and push the tail of the audio.
 *
 * Resolves once the server has the last slice, or throws if it could not be
 * delivered — check-out waits on this, because audio dropped here is the exact
 * failure this module exists to prevent.
 */
export async function stopVisitRecording(visitId: number): Promise<void> {
    if (activeVisitId !== visitId || !recorder) return;

    const rec = recorder;
    if (rec.state !== 'inactive') {
        await new Promise<void>((resolve) => {
            rec.onstop = () => resolve();
            try { rec.requestData(); } catch { /* flush buffered audio before stopping */ }
            rec.stop();
        });
        // Let the final ondataavailable land before the last flush.
        await new Promise(r => setTimeout(r, 300));
    }

    if (flushTimer) { clearInterval(flushTimer); flushTimer = null; }
    if (tickTimer) { clearInterval(tickTimer); tickTimer = null; }

    await flush();
    const undelivered = pending.length > 0;
    const err = uploadError;
    teardown();
    if (undelivered) throw new Error(err || 'The end of the recording could not be uploaded. Check your connection and try again.');
}

/**
 * Put the mic back on a visit that is checked in but not being recorded.
 *
 * Two cases, both of which end with a rep unable to check out otherwise:
 *  - the app was reloaded or killed mid-visit, so there is a resume record and
 *    a partial recording already on the server; the session continues.
 *  - the visit was checked in before recording was enforced (or on a build that
 *    never asked for the mic), so there is no record at all. A single open visit
 *    is adopted and recorded from here on, which is what lets it be checked out.
 *
 * Returns the visit it took over, or null. Never throws: a refused mic here
 * leaves things exactly as they were.
 */
export async function resumeInterruptedRecording(inProgressVisitIds: number[]): Promise<number | null> {
    if (activeVisitId !== null) return null;   // already recording; nothing to resume

    const prior = readResume();
    if (prior && !inProgressVisitIds.includes(prior.visitId)) {
        // That visit is finished (or not this rep's) — the record is stale.
        clearResume();
    }
    const resuming = prior !== null && inProgressVisitIds.includes(prior.visitId);
    // Adopt an orphaned visit only when there is exactly one, so the mic can
    // never land on the wrong customer.
    const target = resuming ? prior!.visitId : (inProgressVisitIds.length === 1 ? inProgressVisitIds[0] : null);
    if (target === null) return null;

    try {
        const mic = await acquireMic();
        startVisitRecording(target, mic, resuming);
        return target;
    } catch {
        // Still checked in, and still recordable once the mic comes back. Leave
        // any resume record in place so the next app start tries again.
        return null;
    }
}

/**
 * Put the mic on the visit the rep just opened for check-out.
 *
 * `resumeInterruptedRecording` refuses to guess when several visits are open at
 * once, so every visit but the one in the resume record stays unrecorded — and
 * therefore un-checkoutable. The rep is told to stay on the visit, but the app
 * never asks for the mic, so there is nothing they can do about it. Opening the
 * check-out form names a customer explicitly, so the mic can be attached here
 * without guessing which visit it belongs to.
 *
 * Audio starts flowing immediately, so `recording_path` is claimed while the
 * rep fills the form and the check-out that follows is accepted. Returns false
 * if the mic is refused, which leaves check-out to fail on the server exactly
 * as it did before.
 */
export async function ensureRecordingFor(visitId: number): Promise<boolean> {
    if (activeVisitId === visitId) return true;
    try {
        const mic = await acquireMic();
        startVisitRecording(visitId, mic, readResume()?.visitId === visitId);
        return true;
    } catch {
        return false;
    }
}

/** Last-ditch flush when the tab or app is going away. */
if (typeof window !== 'undefined') {
    window.addEventListener('pagehide', () => { void flush(); });
    document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'hidden') void flush(); });
}
